import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import OrderShareBar from "@/components/OrderShareBar";
import { ArrowLeft, CheckCircle2, Trash2, X, RotateCcw, Activity, History, Pencil } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const STATUS_META = {
  active: { label: "ACTIVE", bg: "#E63946", fg: "#FFFFFF", Icon: Activity },
  completed: { label: "COMPLETED", bg: "#2A9D8F", fg: "#FFFFFF", Icon: CheckCircle2 },
  deleted: { label: "DELETED", bg: "#0A0A0A", fg: "#FFFFFF", Icon: Trash2 },
};

export default function OrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [adminPhone, setAdminPhone] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const reload = () =>
    api.get(`/orders/${id}`).then((r) => setOrder(r.data)).catch(() => navigate("/orders"));

  useEffect(() => {
    reload();
    api.get("/config/admin-phone").then((r) => setAdminPhone(r.data.admin_phone)).catch(() => {});
    // eslint-disable-next-line
  }, [id]);

  if (!order) return <div className="brut-card p-6">LOADING…</div>;

  const status = order.status || "active";
  const meta = STATUS_META[status];

  const handleComplete = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/orders/${id}/complete`);
      setOrder(data);
      toast.success("Order marked as completed");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const handleDelete = async () => {
    if (!reason.trim()) { toast.error("Reason is required"); return; }
    setBusy(true);
    try {
      const { data } = await api.patch(`/orders/${id}/delete`, { reason: reason.trim() });
      setOrder(data);
      setDeleteOpen(false);
      setReason("");
      toast.success("Order deleted");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const handleReactivate = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/orders/${id}/reactivate`);
      setOrder(data);
      toast.success("Order reactivated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-4xl mx-auto" data-testid="order-detail-page">
      <button onClick={() => navigate(-1)} className="brut-btn brut-btn-secondary !py-2 text-xs mb-4" data-testid="back-btn">
        <ArrowLeft size={14} strokeWidth={3} /> BACK
      </button>

      <div className="brut-card p-6 mb-4">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-4">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <div className="text-xs uppercase tracking-[0.2em] text-[#E63946] font-bold">── ORDER</div>
              <span data-testid="status-badge"
                    className="inline-flex items-center gap-1 px-2 py-1 border-2 border-black text-xs font-bold uppercase tracking-wider"
                    style={{ background: meta.bg, color: meta.fg }}>
                <meta.Icon size={12} strokeWidth={3} /> {meta.label}
              </span>
            </div>
            <h1 className="font-heading font-black text-4xl tracking-tighter">{order.order_no}</h1>
            <div className="text-xs text-[#666] mt-1">{new Date(order.created_at).toLocaleString()} · by {order.created_by_name}</div>
          </div>
        </div>

        <div className="mb-5">
          <OrderShareBar order={order} adminPhone={adminPhone} />
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
          {status !== "deleted" && (
            <button onClick={() => navigate(`/orders/${id}/edit`)} disabled={busy}
                    className="brut-btn brut-btn-dark !py-2 text-xs" data-testid="edit-btn">
              <Pencil size={14} strokeWidth={3} /> EDIT ORDER
            </button>
          )}
          {status === "active" && (
            <button onClick={handleComplete} disabled={busy} className="brut-btn brut-btn-primary !py-2 text-xs"
                    data-testid="complete-btn" style={{ background: "#2A9D8F" }}>
              <CheckCircle2 size={14} strokeWidth={3} /> MARK COMPLETED
            </button>
          )}
          {status !== "deleted" && (
            <button onClick={() => setDeleteOpen(true)} disabled={busy}
                    className="brut-btn brut-btn-secondary !py-2 text-xs text-[#E63946]" data-testid="delete-btn">
              <Trash2 size={14} strokeWidth={3} /> DELETE
            </button>
          )}
          {status === "deleted" && user?.role === "admin" && (
            <button onClick={handleReactivate} disabled={busy} className="brut-btn brut-btn-dark !py-2 text-xs" data-testid="reactivate-btn">
              <RotateCcw size={14} strokeWidth={3} /> RESTORE
            </button>
          )}
        </div>

        <div className="grid sm:grid-cols-4 gap-3 mb-4">
          <Field label="Customer" value={order.customer.name} />
          <Field label="City" value={order.customer.city} />
          <Field label="Phone" value={order.customer.phone} />
          <Field label="Agent" value={`${order.customer.agent}${order.customer.agent_phone ? ` · ${order.customer.agent_phone}` : ""}`} />
        </div>

        <table className="w-full text-sm">
          <thead className="border-b-2 border-black"><tr className="text-left text-xs uppercase">
            <th className="py-2">Design</th><th>Qty</th><th>Unit</th><th>Rate</th><th>Amount</th><th>Photo</th>
          </tr></thead>
          <tbody>
            {order.items.map((it, i) => (
              <tr key={i} className="border-b border-black/15">
                <td className="py-2 font-bold">{it.design_number}</td>
                <td>{it.quantity}</td>
                <td>{it.unit || "PCS"}</td>
                <td>₹{it.rate}</td>
                <td>₹{it.quantity * it.rate}</td>
                <td>{it.photo_path ? <PhotoThumb path={it.photo_path} /> : "—"}</td>
              </tr>
            ))}
          </tbody>
          <tfoot className="border-t-2 border-black">
            <tr className="font-mono text-sm">
              <td className="py-2">Subtotal</td>
              <td>{order.total_quantity}</td>
              <td className="text-xs">
                {order.total_pcs ? `${order.total_pcs} PCS` : ""}
                {order.total_pcs && order.total_sets ? " + " : ""}
                {order.total_sets ? `${order.total_sets} SETS` : ""}
              </td>
              <td></td>
              <td>₹{Math.round(order.total_amount).toLocaleString("en-IN")}</td><td></td>
            </tr>
            <tr className="font-mono text-sm">
              <td className="py-2" colSpan="4">GST @ {Math.round((order.gst_rate || 0.05) * 100)}%</td>
              <td>₹{Math.round(order.gst_amount || (order.total_amount * 0.05)).toLocaleString("en-IN")}</td><td></td>
            </tr>
            <tr className="font-heading font-black">
              <td className="py-2" colSpan="4">GRAND TOTAL</td>
              <td>₹{Math.round(order.grand_total || (order.total_amount * 1.05)).toLocaleString("en-IN")}</td><td></td>
            </tr>
          </tfoot>
        </table>

        {order.remarks && (
          <div className="mt-4 border-t-2 border-black pt-3">
            <div className="label-mono">Remarks</div>
            <div className="text-sm">{order.remarks}</div>
          </div>
        )}

        {status === "deleted" && order.deleted_reason && (
          <div className="mt-4 border-t-2 border-black pt-3 bg-[#FFEDED] -mx-6 px-6 pb-3">
            <div className="label-mono text-[#E63946]">Deletion Reason</div>
            <div className="text-sm">{order.deleted_reason}</div>
            <div className="text-xs text-[#666] mt-1">by {order.deleted_by_name} · {order.deleted_at && new Date(order.deleted_at).toLocaleString()}</div>
          </div>
        )}
      </div>

      {/* Audit log */}
      <div className="brut-card p-6" data-testid="audit-log">
        <div className="flex items-center gap-2 mb-4">
          <History strokeWidth={3} size={18} />
          <div className="font-heading font-black text-xl">ACTION LOG</div>
        </div>
        <div className="space-y-3">
          {(order.audit_log || []).slice().reverse().map((e, i) => (
            <div key={i} className="border-2 border-black p-3 flex items-start gap-3" data-testid={`audit-entry-${e.action}`}>
              <div className="text-xs font-bold uppercase tracking-wider px-2 py-1 border-2 border-black"
                   style={{ background: STATUS_META[e.action === "reactivated" ? "active" : (e.action === "created" ? "active" : e.action)]?.bg || "#0A0A0A", color: "#fff" }}>
                {e.action}
              </div>
              <div className="flex-1">
                <div className="text-sm font-bold">{e.by_name} <span className="font-mono text-xs text-[#666]">&lt;{e.by_email}&gt;</span></div>
                <div className="text-xs text-[#666] uppercase tracking-wider">{new Date(e.at).toLocaleString()}</div>
                {e.note && <div className="text-sm mt-1">{e.note}</div>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Delete reason modal */}
      {deleteOpen && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" data-testid="delete-modal">
          <div className="brut-card w-full max-w-md">
            <div className="flex items-center justify-between border-b-2 border-black px-4 py-3">
              <div className="font-heading font-black text-lg">DELETE ORDER</div>
              <button onClick={() => setDeleteOpen(false)} className="brut-btn brut-btn-secondary !py-1 !px-2" data-testid="delete-close-btn">
                <X size={14} strokeWidth={3} />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <div className="text-sm">Order <b>{order.order_no}</b> will be moved to <b>Deleted</b>. This action is logged.</div>
              <label className="label-mono">Reason (required)</label>
              <textarea
                data-testid="delete-reason-input"
                className="brut-input min-h-[100px]"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Cancelled by customer, duplicate order, …"
              />
              <div className="flex gap-3 pt-2">
                <button onClick={() => setDeleteOpen(false)} className="brut-btn brut-btn-secondary flex-1" data-testid="delete-cancel-btn">
                  CANCEL
                </button>
                <button onClick={handleDelete} disabled={busy} className="brut-btn brut-btn-primary flex-1" data-testid="delete-confirm-btn">
                  <Trash2 size={14} strokeWidth={3} /> {busy ? "DELETING…" : "CONFIRM DELETE"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Field = ({ label, value }) => (
  <div className="border-2 border-black p-3">
    <div className="label-mono !mb-1">{label}</div>
    <div className="text-sm font-bold">{value}</div>
  </div>
);

const PhotoThumb = ({ path }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  useEffect(() => {
    let mounted = true;
    api.get(`/files/${path}`, { responseType: "blob" }).then((r) => {
      if (mounted) setBlobUrl(URL.createObjectURL(r.data));
    }).catch(() => { /* noop */ });
    return () => { mounted = false; if (blobUrl) URL.revokeObjectURL(blobUrl); };
    // eslint-disable-next-line
  }, [path]);
  if (!blobUrl) return <span className="text-xs text-[#666]">…</span>;
  return <img src={blobUrl} alt="" className="w-12 h-12 border-2 border-black object-cover" />;
};
